import { Icons } from "@ir/ui";

interface IDeductionChip {
    shape?: "circle" | "square";
    withIcon?: boolean;
    iconSize?: "small" | "medium" | "large";
    withBorder?: boolean;
    withBackground?: boolean;
    size?: "small" | "medium" | "large";
    style?: React.CSSProperties;
    className?: string;
}

type ColorScheme = {
    text: string;
    bg: string;
    border: string;
};

// Progress Chip
enum StageKey {
    DOCUMENTATION = "DOCUMENTATION",
    ANALYSIS = "ANALYSIS",
    RESOLUTION = "RESOLUTION",
    CLOSED = "CLOSED",
}

enum StatusKey {
    NEW = "NEW",
    BACKUP_REQUESTED = "BACKUP_REQUESTED",
    SALES_INFO_REQUESTED = "SALES_INFO_REQUESTED",
    VALID = "VALID",
    INVALID = "INVALID",
    AGED = "AGED",
    DUPLICATE = "DUPLICATE",
    DIFFERENT_PURCHASER = "DIFFERENT_PURCHASER",
    DISPUTED = "DISPUTED",
    DISPUTE_RAISED = "DISPUTE_RAISED",
    PARTIALLY_DISPUTED = "PARTIALLY_DISPUTED",
    DISPUTE_APPROVED = "DISPUTE_APPROVED",
    DISPUTE_DENIED = "DISPUTE_DENIED",
    DISPUTE_NEEDED = "DISPUTE_NEEDED",
    RE_PAYMENT_DONE = "RE-PAYMENT_DONE",
    APPROVED = "APPROVED",
    PENDING = "PENDING",
    POD_REQUESTED = "POD_REQUESTED",
    PENDING_REVIEW = "PENDING_REVIEW",
    REJECTED_BY_APPROVER = "REJECTED_BY_APPROVER",
    REJECTED_BY_REVIEWER = "REJECTED_BY_REVIEWER",
    PENDING_MANAGEMENT = "PENDING_MANAGEMENT",
    RECORD_UPDATED = "RECORD_UPDATED",
    REJECTED_BY_MANAGEMENT = "REJECTED_BY_MANAGEMENT",
}

const STAGE_ICONS: Record<StageKey, Icons | null> = {
    [StageKey.DOCUMENTATION]: Icons.File,
    [StageKey.ANALYSIS]: Icons.Search,
    [StageKey.RESOLUTION]: Icons.Flash,
    [StageKey.CLOSED]: Icons.Check,
};

// Priority Chip
enum PriorityKey {
    HIGHEST = "HIGHEST",
    HIGH = "HIGH",
    MEDIUM = "MEDIUM",
    LOW = "LOW",
}

const PRIORITY_ICONS: Record<PriorityKey, Icons | null> = {
    [PriorityKey.HIGHEST]: Icons.ChevronUpDouble,
    [PriorityKey.HIGH]: Icons.ChevronUp,
    [PriorityKey.MEDIUM]: Icons.Minus,
    [PriorityKey.LOW]: Icons.ChevronDown,
} as const;

const PRIORITY_COLORS: Record<PriorityKey, ColorScheme> = {
    [PriorityKey.HIGHEST]: { text: "error-700", bg: "error-10", border: "error-300" }, // 1
    [PriorityKey.HIGH]: { text: "error-500", bg: "error-10", border: "error-200" }, // 2
    [PriorityKey.MEDIUM]: { text: "warning-400", bg: "warning-10", border: "warning-100" }, // 3
    [PriorityKey.LOW]: { text: "info-500", bg: "info-10", border: "info-100" }, // 4
} as const;

// Validity Chip
enum ValidityKey {
    VALID = "VALID",
    INVALID = "INVALID",
    PARTIALLY_INVALID = "PARTIALLY_INVALID",
}

const VALIDITY_ICONS: Record<ValidityKey, Icons | null> = {
    [ValidityKey.VALID]: Icons.Check,
    [ValidityKey.INVALID]: Icons.Close,
    [ValidityKey.PARTIALLY_INVALID]: Icons.Menu05,
} as const;

const VALIDITY_COLORS: Record<ValidityKey, ColorScheme> = {
    [ValidityKey.VALID]: { text: "success-500", bg: "success-10", border: "success-25" }, // 1
    [ValidityKey.INVALID]: { text: "error-700", bg: "warning-10", border: "warning-200" }, // 2
    [ValidityKey.PARTIALLY_INVALID]: { text: "info-500", bg: "info-10", border: "info-25" }, // 3
} as const;

export type {
    IDeductionChip,
    ColorScheme,
}

export {
    StageKey,
    StatusKey,
    STAGE_ICONS,
    PriorityKey,
    PRIORITY_ICONS,
    PRIORITY_COLORS,
    ValidityKey,
    VALIDITY_ICONS,
    VALIDITY_COLORS,
}